import * as XLSX from 'xlsx';

const MAX_EXPORT_SIZE_MB = 5;
const MAX_EXPORT_SIZE_BYTES = MAX_EXPORT_SIZE_MB * 1024 * 1024;
const exportSizeLimitDebug = { forceError: false };

export const setExportSizeLimitDebug = (enabled = false) => {
  exportSizeLimitDebug.forceError = Boolean(enabled);
};

export const isExportSizeLimitDebugEnabled = () => exportSizeLimitDebug.forceError;

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__setExportSizeLimitDebug = setExportSizeLimitDebug;
  window.__getExportSizeLimitDebug = isExportSizeLimitDebugEnabled;
}

const toDigits = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).replace(/\D/g, '');
};

const stripControlChars = (rawValue) => {
  const value = String(rawValue || '');
  let output = '';

  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code >= 32) {
      output += value[i];
    }
  }

  return output;
};

const sanitizeFileNamePart = (value) => {
  const normalized = stripControlChars(value)
    .trim()
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return normalized || 'empresa';
};

const getCompanyNameFromLocalStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return '';
  }

  const raw = window.localStorage.getItem('SelectedCompany')
    || window.localStorage.getItem('selectedCompany');

  if (!raw) {
    return '';
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      return parsed;
    }

    return parsed?.name
      || parsed?.nome
      || parsed?.nomeCliente
      || parsed?.companyName
      || '';
  } catch {
    return raw;
  }
};

export const exportLeadsToXlsx = ({
  leads = [],
  formatDate,
  fileNamePrefix = 'leads-filtrados',
  filters = {},
  idCRM,
}) => {
  const safeLeads = Array.isArray(leads) ? leads : [];
  const normalizedFilters = { ...filters };
  const today = new Date().toISOString().slice(0, 10);

  if (normalizedFilters.dateFilter === 'Datas Específicas' && normalizedFilters.startDate && !normalizedFilters.endDate) {
    normalizedFilters.endDate = today;
  }

  const inferredCrmId = safeLeads.find((lead) => lead?.idCRM !== undefined && lead?.idCRM !== null)?.idCRM;
  const effectiveCrmId = idCRM !== undefined && idCRM !== null ? idCRM : inferredCrmId;
  const showCvStatusColumn = effectiveCrmId === undefined || effectiveCrmId === null || Number(effectiveCrmId) === 1;

  const buildFiltersRows = (filtersData) => {
    const rows = [['Filtros aplicados']];

    const entries = [
      ['Situação', filtersData.situacaoFilter],
      ['Motivo de descarte', filtersData.descarteFilter],
      ['ID Lead', filtersData.idLeadFilter],
      ['Nome', filtersData.nomeFilter],
      ['WhatsApp ID', filtersData.whatsAppIdFilter],
      ['Empreendimento', filtersData.empreendimentoFilter],
      ['Período', filtersData.dateFilter],
      ['Data início', filtersData.startDate],
      ['Data fim', filtersData.endDate],
    ];

    const hasAny = entries.some(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');

    if (!hasAny) {
      rows.push(['Nenhum filtro aplicado']);
      return [...rows, ['']];
    }

    entries.forEach(([label, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        rows.push([label, String(value)]);
      }
    });

    return [...rows, ['']];
  };

  const header = [
    'ID Lead',
    'Nome',
    'WhatsAppId',
    'Situacao Interna',
    'Empreendimento',
    'Motivo Descarte',
    'Data Cadastro',
    'Tentativas de Cadência',
  ];
  if (showCvStatusColumn) {
    header.splice(7, 0, 'Url para ver Status do Lead no CV');
  }

  const companyName = getCompanyNameFromLocalStorage();
  const companyDisplayName = companyName || 'Empresa não informada';

  const companyRows = [
    [`Empresa: ${companyDisplayName}`],
    [''],
  ];
  const filtersRows = buildFiltersRows(normalizedFilters);
  const rows = safeLeads.map((lead) => {
    const dataCadastro = lead.dataHoraCadastroAtualizado
      ? (typeof formatDate === 'function'
        ? formatDate(lead.dataHoraCadastroAtualizado)
        : lead.dataHoraCadastroAtualizado)
      : '';

    const baseCvUrl = lead.cvUrlBase || '';
    const cvStatusUrl = (baseCvUrl && lead.idLead !== undefined && lead.idLead !== null)
      ? `${baseCvUrl}gestor/comercial/leads/${lead.idLead}/administrar`
      : '';

    const row = [
      lead.idLead !== undefined && lead.idLead !== null
        ? Number(lead.idLead)
        : '',
      lead.nome || '',
      toDigits(lead.whatsAppId),
      lead.situacaoInterna || '',
      lead.nomeEmpreendimento || '',
      lead.motivoDescarte || '',
      dataCadastro,
      lead.tentativasContato !== undefined && lead.tentativasContato !== null
        ? Number(lead.tentativasContato)
        : '',
    ];
    if (showCvStatusColumn) {
      row.splice(7, 0, cvStatusUrl);
    }
    return row;
  });

  const worksheet = XLSX.utils.aoa_to_sheet([...companyRows, ...filtersRows, header, ...rows]);
  const columns = [
    { wpx: 75 },  // ID Lead
    { wpx: 150 }, // Nome
    { wpx: 145 }, // WhatsAppId
    { wpx: 125 }, // Situacao Interna
    { wpx: 180 }, // Empreendimento
    { wpx: 180 }, // Motivo Descarte
    { wpx: 130 }, // Data Cadastro
    { wpx: 170 }, // Tentativas de Cadência
  ];
  if (showCvStatusColumn) {
    columns.splice(7, 0, { wpx: 280 });
  }
  worksheet['!cols'] = columns;
  worksheet['!rows'] = [{ hpx: 32 }];
  worksheet['!merges'] = [
    {
      s: { r: 0, c: 0 },
      e: { r: 0, c: header.length - 1 },
    },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads');

  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const companyFileNamePart = sanitizeFileNamePart(companyDisplayName);
  const fileName = `${fileNamePrefix}-${companyFileNamePart}-${timestamp}.xlsx`;

  const fileArray = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
    compression: true,
  });
  const fileSizeBytes = fileArray?.byteLength || fileArray?.length || 0;

  if (fileSizeBytes > MAX_EXPORT_SIZE_BYTES || exportSizeLimitDebug.forceError) {
    const fileSizeMb = (fileSizeBytes / (1024 * 1024)).toFixed(2);
    return {
      ok: false,
      error: exportSizeLimitDebug.forceError
        ? `Exportação cancelada (modo teste). O arquivo gerado tem ${fileSizeMb} MB.`
        : `Exportação cancelada. O arquivo gerado tem ${fileSizeMb} MB e ultrapassa o limite de ${MAX_EXPORT_SIZE_MB} MB. Aplique mais filtros e tente novamente.`,
    };
  }

  XLSX.writeFile(workbook, fileName, {
    compression: true,
  });

  return { ok: true };
};
